import React, { useMemo } from 'react';
import { handleResponseLanguage } from '../../../../../i18n';
import styled from 'styled-components';
import { Grid } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';

// Components
import SelectionItem from './SelectionItem';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
  pushToSelectedGuarantees,
  popFromSelectedGuarantees,
} from '../../store/redux/slices/selectionSlice';

// Types
import { RootState } from '../../store/redux/reducers/rootReducer';
import { Guarantee } from '../../../../../types/Quotes';
import cogoToast from 'cogo-toast';
import SelectAllContainer from './SelectAllContainer';
import IllustrationIconSelector from '../../../../common/src/components/IllustrationIconSelector';

interface Props {
  riskObject: string | undefined;
  guarantees: Guarantee[] | undefined;
}

const GuaranteesSelection: React.FC<React.PropsWithChildren<Props>> = ({
  riskObject = '',
  guarantees,
}) => {
  const { t } = useTranslation();
  const { selectedGuarantees } = useSelector(
    (state: RootState) => state.selection,
  );
  const dispatch = useDispatch();

  const handleOnSelected = (
    name: string,
    mandatory: boolean,
    required?: string[],
  ) => {
    if (mandatory)
      return cogoToast.info(t('This guarantee cannot be unchecked'));

    // Check for required on current guarantee
    if (
      required &&
      required.some(
        (guarantee) => !selectedGuarantees[riskObject]?.includes(guarantee),
      )
    ) {
      dispatch(pushToSelectedGuarantees({ items: required, riskObject }));
      cogoToast.info(
        t(
          'Some guarantees were automatically checked because they are required for the chosen guarantee',
        ),
      );
    }

    // Check if guarantee is required on other guarantees
    const dependencies =
      guarantees
        ?.filter(({ required }) => required?.includes(name))
        .map(({ tag }) => tag) || [];
    if (
      dependencies.length > 0 &&
      dependencies.some((guarantee) =>
        selectedGuarantees[riskObject]?.includes(guarantee),
      )
    ) {
      dispatch(popFromSelectedGuarantees({ items: dependencies, riskObject }));
      cogoToast.info(
        t(
          'Some guarantees were automatically unchecked they can not be taken without the other',
        ),
      );
    }

    if (selectedGuarantees[riskObject]?.includes(name))
      return dispatch(
        popFromSelectedGuarantees({
          items: name,
          riskObject,
        }),
      );
    return dispatch(
      pushToSelectedGuarantees({
        items: name,
        riskObject,
      }),
    );
  };

  /**
   * Makes sure that the correct icon is returned for a specific guarantee
   * @param riskObject Risk object of the tag
   * @param tag The guarantee tag
   * @returns Adjusted tag
   */
  const getTagType = (riskObject: string, tag: string) => {
    if (
      riskObject?.toLowerCase() === 'family' &&
      tag?.toLowerCase() === 'civil_liability'
    )
      return 'civil_liability_family';
    else if (
      riskObject?.toLowerCase() === 'funeral' &&
      tag?.toLowerCase() === 'assistance'
    )
      return 'funeral_assistance';

    return tag;
  };

  const isDisabled = (tag: string) => {
    // Check for oneOf
    const oneOfGuarantees =
      guarantees?.filter(({ one_of }) => one_of).map(({ tag }) => tag) || [];
    if (oneOfGuarantees.length > 0) {
      if (
        oneOfGuarantees?.some((guarantee) =>
          selectedGuarantees[riskObject].includes(guarantee),
        )
      ) {
        return false;
      }
      return !oneOfGuarantees.includes(tag);
    }
    return false;
  };

  const isAllSelected =
    guarantees?.every(({ tag }) =>
      selectedGuarantees[riskObject].includes(tag),
    ) || false;

  const handleOnSelectAll = () => {
    if (isAllSelected)
      dispatch(
        popFromSelectedGuarantees({
          items: selectedGuarantees[riskObject]
            .filter(
              (_tag) => !guarantees?.find(({ tag }) => tag === _tag)?.mandatory,
            )
            .map((tag) => tag),
          riskObject,
        }),
      );
    else
      dispatch(
        pushToSelectedGuarantees({
          items:
            guarantees
              ?.filter(
                ({ tag }) => !selectedGuarantees[riskObject].includes(tag),
              )
              .map(({ tag }) => tag) || [],
          riskObject,
        }),
      );
  };

  if (!guarantees) return <div>Risk object unsupported</div>;

  const allMandatory = useMemo(
    () => guarantees?.every(({ mandatory }) => mandatory),
    [guarantees],
  );

  return (
    <Grid
      templateColumns={{
        base: '100%',
        tablet: 'repeat(auto-fill, 36rem)',
      }}
      gridGap="6"
      justifyContent="center"
      data-test-id="ProdFac_GuaranteesSelection"
    >
      <StyledSelectAllContainer
        onClick={handleOnSelectAll}
        allSelected={isAllSelected}
        disabled={allMandatory}
      />
      {guarantees?.map(({ tag, title, description, mandatory, required }) => (
        <SelectionItem
          key={`${riskObject}_guarantees_selection_item_${tag}`}
          title={handleResponseLanguage(title)}
          type={tag}
          selected={selectedGuarantees[riskObject]?.includes(tag)}
          onSelected={() => handleOnSelected(tag, mandatory, required)}
          icon={<IllustrationIconSelector type={getTagType(riskObject, tag)} />}
          mandatory={mandatory}
          disabled={isDisabled(tag)}
          disableMessage={t(
            'You must choose one of the mandatory guarantees first',
          )}
          data-test-id={`ADVISORY-CONVO_SELECTION-VIEW_SELECTION-ITEM_GUARANTEE-${tag}`}
        >
          {handleResponseLanguage(description)}
        </SelectionItem>
      ))}
    </Grid>
  );
};

const StyledSelectAllContainer = styled(SelectAllContainer)`
  grid-column: 1 / -1;
  padding-bottom: 0;
`;

export default GuaranteesSelection;
